import { useState, useMemo, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import {
  Search,
  Plus,
  ArrowRight,
  LayoutDashboard,
  FolderOpen,
  Settings,
  User,
  Users,
  Building2,
  CreditCard,
} from 'lucide-react'
import { cn } from '@/utils/cn'
import { Project, ProjectStatus } from '@/types'
import { isSuperAdmin, hasAdminPrivileges } from '@/utils/permissions'

interface QuickLink {
  id: string
  label: string
  icon: React.ElementType
  path: string
  adminOnly?: boolean
  superAdminOnly?: boolean
}

const QUICK_LINKS: QuickLink[] = [
  { id: 'overview', label: 'Overview', icon: LayoutDashboard, path: '/dashboard?tab=overview' },
  { id: 'projects', label: 'Projects', icon: FolderOpen, path: '/dashboard?tab=projects' },
  { id: 'profile', label: 'Profile', icon: User, path: '/settings/profile' },
  { id: 'preferences', label: 'Preferences', icon: Settings, path: '/settings/preferences' },
  { id: 'team', label: 'Team', icon: Users, path: '/admin/team', adminOnly: true },
  { id: 'billing', label: 'Billing', icon: CreditCard, path: '/admin/billing', adminOnly: true },
  { id: 'agencies', label: 'Agencies', icon: Building2, path: '/admin/agency-management', superAdminOnly: true },
]

const getStatusConfig = (status: ProjectStatus) => {
  const config: Record<ProjectStatus, { bg: string; text: string; dot: string }> = {
    draft: { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
    submitted: { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
    in_development: { bg: 'bg-blue-50', text: 'text-blue-700', dot: 'bg-blue-500' },
    feedback: { bg: 'bg-orange-50', text: 'text-orange-700', dot: 'bg-orange-500' },
    in_management: { bg: 'bg-teal-50', text: 'text-teal-700', dot: 'bg-teal-500' },
    completed: { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
    archived: { bg: 'bg-framed-neutral-100', text: 'text-framed-neutral-600', dot: 'bg-framed-neutral-400' },
  }
  return config[status] || config.draft
}

interface DashboardCommandPaletteProps {
  projects: Project[]
  user: any
  onClose: () => void
  onSelectProject: (projectId: string) => void
  onNewProject: () => void
  onNavigate: (path: string) => void
}

export const DashboardCommandPalette = ({
  projects,
  user,
  onClose,
  onSelectProject,
  onNewProject,
  onNavigate,
}: DashboardCommandPaletteProps) => {
  const [query, setQuery] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  // Commands
  const commands = [
    { id: 'new-project', label: 'New Project', icon: Plus, action: onNewProject },
  ]

  // Role-filtered quick links
  const visibleLinks = useMemo(() => {
    return QUICK_LINKS.filter((link) => {
      if (link.superAdminOnly && !isSuperAdmin(user)) return false
      if (link.adminOnly && !hasAdminPrivileges(user)) return false
      return true
    })
  }, [user])

  const filteredLinks = useMemo(() => {
    if (!query.trim()) return visibleLinks
    const q = query.toLowerCase()
    return visibleLinks.filter(link => link.label.toLowerCase().includes(q))
  }, [query, visibleLinks])

  // Filtered projects
  const filteredProjects = useMemo(() => {
    if (!query.trim()) {
      return [...projects]
        .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
        .slice(0, 6)
    }
    const q = query.toLowerCase()
    return projects.filter(p =>
      (p.name || '').toLowerCase().includes(q) ||
      ((p as any).agencies?.name || '').toLowerCase().includes(q)
    ).slice(0, 10)
  }, [projects, query])

  const totalCount = commands.length + filteredLinks.length + filteredProjects.length

  const getActionAtIndex = (index: number) => {
    if (index < commands.length) {
      return { type: 'command' as const, item: commands[index] }
    }
    const linkIndex = index - commands.length
    if (linkIndex < filteredLinks.length) {
      return { type: 'link' as const, item: filteredLinks[linkIndex] }
    }
    const projectIndex = linkIndex - filteredLinks.length
    if (projectIndex < filteredProjects.length) {
      return { type: 'project' as const, item: filteredProjects[projectIndex] }
    }
    return null
  }

  const executeAtIndex = (index: number) => {
    const result = getActionAtIndex(index)
    if (!result) return
    if (result.type === 'command') {
      result.item.action()
      onClose()
    } else if (result.type === 'link') {
      onNavigate(result.item.path)
      onClose()
    } else {
      onSelectProject(result.item.id)
      onClose()
    }
  }

  useEffect(() => {
    setSelectedIndex(0)
  }, [query])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setSelectedIndex(prev => Math.min(prev + 1, totalCount - 1))
          break
        case 'ArrowUp':
          e.preventDefault()
          setSelectedIndex(prev => Math.max(prev - 1, 0))
          break
        case 'Enter':
          e.preventDefault()
          executeAtIndex(selectedIndex)
          break
        case 'Escape':
          e.preventDefault()
          onClose()
          break
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [selectedIndex, totalCount])

  // Scroll selected into view
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-index="${selectedIndex}"]`)
    el?.scrollIntoView({ block: 'nearest' })
  }, [selectedIndex])

  let flatIndex = 0

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15 }}
      className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh] bg-black/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: -10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: -10 }}
        transition={{ duration: 0.15 }}
        className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search Input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-framed-neutral-300">
          <Search className="w-5 h-5 text-framed-neutral-400 flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search projects, navigate..."
            className="flex-1 bg-transparent text-sm text-framed-neutral-800 placeholder-framed-neutral-400 focus:outline-none"
          />
          <kbd className="hidden sm:inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium text-framed-neutral-400 bg-framed-neutral-100 rounded border border-framed-neutral-300">
            ESC
          </kbd>
        </div>

        {/* Results */}
        <div ref={listRef} className="max-h-[50vh] overflow-y-auto p-2">
          {totalCount === 0 && (
            <div className="py-8 text-center text-sm text-framed-neutral-400">
              No results found
            </div>
          )}

          {/* Commands */}
          {commands.length > 0 && (
            <div className="mb-1">
              <p className="px-3 py-1.5 text-[11px] font-semibold text-framed-neutral-400 uppercase tracking-wider">
                Commands
              </p>
              {commands.map((cmd) => {
                const idx = flatIndex++
                return (
                  <button
                    key={cmd.id}
                    data-index={idx}
                    onClick={() => executeAtIndex(idx)}
                    className={cn(
                      'w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors',
                      selectedIndex === idx
                        ? 'bg-framed-yellow-100 text-framed-neutral-800'
                        : 'text-framed-neutral-700 hover:bg-framed-neutral-50'
                    )}
                  >
                    <cmd.icon className="w-4 h-4 text-framed-neutral-400" />
                    <span className="flex-1 text-left">{cmd.label}</span>
                    <ArrowRight className="w-3.5 h-3.5 text-framed-neutral-400" />
                  </button>
                )
              })}
            </div>
          )}

          {/* Quick Links */}
          {filteredLinks.length > 0 && (
            <div className="mb-1">
              <p className="px-3 py-1.5 text-[11px] font-semibold text-framed-neutral-400 uppercase tracking-wider">
                Go to
              </p>
              {filteredLinks.map((link) => {
                const idx = flatIndex++
                return (
                  <button
                    key={link.id}
                    data-index={idx}
                    onClick={() => executeAtIndex(idx)}
                    className={cn(
                      'w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors',
                      selectedIndex === idx
                        ? 'bg-framed-yellow-100 text-framed-neutral-800'
                        : 'text-framed-neutral-700 hover:bg-framed-neutral-50'
                    )}
                  >
                    <link.icon className="w-4 h-4 text-framed-neutral-400" />
                    <span className="flex-1 text-left">{link.label}</span>
                    <ArrowRight className="w-3.5 h-3.5 text-framed-neutral-400" />
                  </button>
                )
              })}
            </div>
          )}

          {/* Projects */}
          {filteredProjects.length > 0 && (
            <div className="mb-1">
              <p className="px-3 py-1.5 text-[11px] font-semibold text-framed-neutral-400 uppercase tracking-wider">
                Projects
              </p>
              {filteredProjects.map((project) => {
                const idx = flatIndex++
                const statusConfig = getStatusConfig(project.status)
                return (
                  <button
                    key={project.id}
                    data-index={idx}
                    onClick={() => executeAtIndex(idx)}
                    className={cn(
                      'w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors',
                      selectedIndex === idx
                        ? 'bg-framed-yellow-100 text-framed-neutral-800'
                        : 'text-framed-neutral-700 hover:bg-framed-neutral-50'
                    )}
                  >
                    <FolderOpen className="w-4 h-4 text-framed-neutral-400 flex-shrink-0" />
                    <span className="flex-1 text-left truncate">{project.name || 'Untitled'}</span>
                    {isSuperAdmin(user) && (project as any).agencies?.name && (
                      <span className="text-xs text-framed-neutral-400 flex-shrink-0 hidden sm:block">
                        {(project as any).agencies.name}
                      </span>
                    )}
                    <span className={cn(
                      'w-2 h-2 rounded-full flex-shrink-0',
                      statusConfig.dot
                    )} />
                  </button>
                )
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t border-framed-neutral-300 flex items-center gap-4 text-[11px] text-framed-neutral-400">
          <span className="flex items-center gap-1">
            <kbd className="px-1 py-0.5 bg-framed-neutral-100 rounded text-[10px] border border-framed-neutral-300">↑↓</kbd>
            Navigate
          </span>
          <span className="flex items-center gap-1">
            <kbd className="px-1 py-0.5 bg-framed-neutral-100 rounded text-[10px] border border-framed-neutral-300">↵</kbd>
            Open
          </span>
          <span className="flex items-center gap-1">
            <kbd className="px-1 py-0.5 bg-framed-neutral-100 rounded text-[10px] border border-framed-neutral-300">Esc</kbd>
            Close
          </span>
        </div>
      </motion.div>
    </motion.div>
  )
}
